import React, { useContext, useEffect, useState } from 'react';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import '../../pages/FiscalizacionReel.css';
import { IconButton } from '@material-ui/core';
import { FiscalizacionContext } from './FiscalizacionContext';
import {
  CalificacionVisitaPS,
  DatoRelevamientoRutina,
  PromedioEncuestaPS,
  ReclamoUltimoTrimestre,
  ResumenFiscalizacionPS,
  getResumenFiscalizacionPS,
} from 'src/services/Fiscalizacion/RelevamientoRutina.services';
import { EncuestaBajaItem, ReclamoClienteItem } from 'src/services/inicioKpis';

const ENCUESTA_CATEGORIAS: { key: 'colaboradores' | 'limpieza' | 'equipos' | 'productos' | 'fiscal'; label: string }[] = [
  { key: 'colaboradores', label: 'Colaboradores' },
  { key: 'limpieza', label: 'Limpieza' },
  { key: 'equipos', label: 'Equipos' },
  { key: 'productos', label: 'Productos' },
  { key: 'fiscal', label: 'Fiscal' },
];


const RelevamientoRutinaFiscalizacion: React.FC= () => {
  const [resumen, setResumen] = useState<ResumenFiscalizacionPS>({
    relevamientos: [],
    reclamos_ultimo_trimestre: [],
    calificacion_visita: {
      evaluacion_id: null,
      fecha_visita: null,
      calificacion: null,
    },
    promedio_encuesta: {
      promedio_calificacion: null,
    },
  });
  const [abierto, setAbierto] = useState(false);

  const fiscalizacionContext = useContext(FiscalizacionContext);
  if (!fiscalizacionContext) {
    throw new Error('useFiscalizacion must be used within a FiscalizacionProvider');
  }
  const { puntoServicioFiscalizacion } = fiscalizacionContext;

  useEffect(() => {
    const psCodigo =
      typeof (puntoServicioFiscalizacion as unknown) === 'string'
        ? String(puntoServicioFiscalizacion || '')
        : String(puntoServicioFiscalizacion?.ps_codigo || '');

    if (!psCodigo) {
      setResumen({
        relevamientos: [],
        reclamos_ultimo_trimestre: [],
        calificacion_visita: {
          evaluacion_id: null,
          fecha_visita: null,
          calificacion: null,
        },
        promedio_encuesta: {
          promedio_calificacion: null,
        },
      });
      return;
    }

    getResumenFiscalizacionPS({ ps_codigo: psCodigo })
      .then((res) => {
        setResumen(res);
      })
      .catch((err) => {
        console.log('error', err);
        setResumen({
          relevamientos: [],
          reclamos_ultimo_trimestre: [],
          calificacion_visita: {
            evaluacion_id: null,
            fecha_visita: null,
            calificacion: null,
          },
          promedio_encuesta: {
            promedio_calificacion: null,
          },
        });
      });
  }, [puntoServicioFiscalizacion]);

  const handleToggle = () => {
    setAbierto(!abierto);
  };

  const renderSimpleTable = (values: string[], prefix: string, emptyLabel: string) => {
    const visibleValues = abierto ? values : values.slice(0, 3);
    return (
      <table className="fiscalizacion-resumen-table">
        <thead>
          <tr>
            <th>{prefix}</th>
          </tr>
        </thead>
        <tbody>
          {(visibleValues.length ? visibleValues : [emptyLabel]).map((valor, i) => (
            <tr key={`${prefix}-${i}`}>
              <td>{valor || emptyLabel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderReclamosTable = (reclamos: ReclamoUltimoTrimestre[]) => {
    const visibleReclamos = abierto ? reclamos : reclamos.slice(0, 3);
    const rows = visibleReclamos.length
      ? visibleReclamos
      : [{ clasificacion_reclamo: 'Sin reclamos', gravedad: '-', cantidad: 0 }];

    return (
      <table className="fiscalizacion-resumen-table">
        <thead>
          <tr>
            <th>Tipo reclamo</th>
            <th>Gravedad</th>
            <th>Cantidad</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((reclamo, index) => (
            <tr key={`${reclamo.clasificacion_reclamo}-${index}`}>
              <td>{reclamo.clasificacion_reclamo}</td>
              <td>{reclamo.gravedad || '-'}</td>
              <td>{reclamo.cantidad}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderCalificacionTable = (calificacionVisita: CalificacionVisitaPS) => (
    <table className="fiscalizacion-resumen-table">
      <thead>
        <tr>
          <th>Calificacion</th>
          <th>Fecha</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{calificacionVisita.calificacion || 'Sin calificacion'}</td>
          <td className="col-nowrap">{calificacionVisita.fecha_visita || '-'}</td>
        </tr>
      </tbody>
    </table>
  );

  const renderPromedioEncuestaTable = (promedioEncuesta: PromedioEncuestaPS) => (
    <table className="fiscalizacion-resumen-table">
      <thead>
        <tr>
          <th>Promedio</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>{promedioEncuesta.promedio_calificacion || 'Sin promedio'}</td>
        </tr>
      </tbody>
    </table>
  );

  const renderEncuestasBajasTable = (encuestas: EncuestaBajaItem[]) => {
    const visibles = abierto ? encuestas : encuestas.slice(0, 3);
    return (
      <table className="fiscalizacion-resumen-table">
        <thead>
          <tr>
            <th className="col-nowrap">Fecha</th>
            <th>Notas ≤ 7</th>
          </tr>
        </thead>
        <tbody>
          {visibles.length === 0 && (
            <tr>
              <td>Sin encuestas bajas</td>
              <td>-</td>
            </tr>
          )}
          {visibles.map((encuesta) => (
            <tr key={`encuesta-${encuesta.caso_id}`}>
              <td className="col-nowrap">{encuesta.fecha || '-'}</td>
              <td>
                {ENCUESTA_CATEGORIAS
                  .filter(({ key }) => encuesta[key] !== null && encuesta[key] !== undefined && (encuesta[key] as number) < 8)
                  .map(({ key, label }) => `${label}: ${encuesta[key]}`)
                  .join(', ')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const renderReclamosClientesTable = (reclamos: ReclamoClienteItem[]) => {
    const visibles = abierto ? reclamos : reclamos.slice(0, 3);
    return (
      <table className="fiscalizacion-resumen-table">
        <thead>
          <tr>
            <th className="col-nowrap">Fecha</th>
            <th>Tipo reclamo</th>
            <th>Conforme</th>
          </tr>
        </thead>
        <tbody>
          {visibles.length === 0 && (
            <tr>
              <td>Sin reclamos</td>
              <td>-</td>
              <td>-</td>
            </tr>
          )}
          {visibles.map((reclamo) => (
            <tr key={`reclamo-cliente-${reclamo.caso_id}`} title={reclamo.descripcion || ''}>
              <td className="col-nowrap">{reclamo.fecha || '-'}</td>
              <td>{reclamo.clasificacion || 'Sin clasificacion'}</td>
              <td>{reclamo.conforme || 'Pendiente'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  const relevamientoValues = resumen.relevamientos.map((item: DatoRelevamientoRutina) => item.rel_codigo?.toString() || 'Sin relevamiento');
  const rutinaValues = resumen.relevamientos.map((item: DatoRelevamientoRutina) => item.rel_numero || 'Sin rutina');
  const encuestasBajas = resumen.encuestas_bajas || [];
  const reclamosClientes = resumen.reclamos_clientes || [];
  const mostrarToggle =
    resumen.relevamientos.length > 3 ||
    resumen.reclamos_ultimo_trimestre.length > 3 ||
    encuestasBajas.length > 3 ||
    reclamosClientes.length > 3;

  return (
    <div className="fisReel">
      <div className="fiscalizacion-resumen-header">
        <h4 style={{ margin: 0 }}>Resumen del punto de servicio</h4>
        {mostrarToggle && (
          <IconButton aria-label="expand row" size="small" onClick={handleToggle}>
            {abierto ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        )}
      </div>
      <div id='reelContainer' className="reel-container fiscalizacion-resumen-grid">
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Relevamiento vigente</div>
          {renderSimpleTable(relevamientoValues, 'Relevamiento', 'Sin relevamiento')}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Rutina vigente</div>
          {renderSimpleTable(rutinaValues, 'Rutina', 'Sin rutina')}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Reclamos ultimo trimestre</div>
          {renderReclamosTable(resumen.reclamos_ultimo_trimestre)}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Calificacion visita</div>
          {renderCalificacionTable(resumen.calificacion_visita)}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Promedio encuesta</div>
          {renderPromedioEncuestaTable(resumen.promedio_encuesta)}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Encuestas ≤ 7 (30 días)</div>
          {renderEncuestasBajasTable(encuestasBajas)}
        </div>
        <div className="reel-wrapper fiscalizacion-resumen-card">
          <div className="title">Reclamos de cliente (30 días)</div>
          {renderReclamosClientesTable(reclamosClientes)}
        </div>
      </div>
    </div>
  );
};

export default RelevamientoRutinaFiscalizacion;
